import clsx from "clsx";
import { FC, useCallback, useMemo } from "react";

import { SectionTitleBar } from "../SectionTitleBar";
import { SectionAssessmentMainView } from "../assessment/SectionAssessmentMainView";

import { DisplayCaption } from "components/DisplayCaption";
import { LoadingScreen } from "components/LoadingScreen";
import { AIDashboardView } from "components/ai/dashboard/AIDashboardView";
import { withActiveAv } from "components/av/AvSubsystemLoader";
import { SECONDARY_OVERLAY_QUERY_PARAM, TAB_QUERY_PARAM } from "components/constants";
import { OutcomesView } from "components/materials/outcomes/OutcomesView";
import { PageSecondarySidebar } from "components/materials/page/PageSecondarySidebar";
import { SectionContentView } from "components/materials/section/content/SectionContentView";
import { SectionPublicationHistory } from "components/materials/section/publication/SectionPublicationHistory";
import { SessionLogView } from "components/materials/section/session-log/SessionLogView";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { TabBar } from "mds/components/TabBar";
import { useIsMdOrLarger, useIsXlOrLarger } from "mds/hooks/use-responsive";
import {
  AssessPointsMarkerIcon,
  BookSectionIcon,
  ClockIcon,
  OutcomeHashtagIcon,
  ReportsIcon,
  RobotAIIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectCurrentSectionIsTemplate,
  selectCurrentSectionIsUnlinked,
  selectCurrentUserIsMPAdmin,
  selectIsPracticeClassSession,
  selectIsRecordingAgent,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { useTrackEventWithOrgAndCourse } from "utils/amplitude";
import { useAv } from "utils/av";
import {
  useBorderedPresentationColoring,
  usePresentation,
  usePresentationCaption,
} from "utils/presentation";
import { useQueryParamsManager } from "utils/routing";
import { CourseContentTabKeys, sectionHomeUrl, withQueryParams } from "utils/urls";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

const LazyScreenshareDisplay = withActiveAv(
  () => import("components/av/ScreenshareDisplay"),
  undefined,
  true,
);

export const SectionView: FC = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isTemplate = useAppSelector(selectCurrentSectionIsTemplate);
  const isUnlinked = useAppSelector(selectCurrentSectionIsUnlinked);
  const isMpAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const { canAuthorSection } = useCapabilities();
  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const { isFollowing } = usePresentation();
  const isSmallScreen = !useIsMdOrLarger();

  const canShowBothSidebars = useIsXlOrLarger();
  const { getQueryParam } = useQueryParamsManager();
  const secondaryOverlay = getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM);
  const isRightSidePanelOpen = secondaryOverlay && secondaryOverlay !== "menu";

  useTrackEventWithOrgAndCourse("Course Overview viewed");

  const { isInVirtualClass } = useAv();
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);

  // Show presentation caption only if the user is in a virtual practice session
  const shouldShowPresentationCaption = isInVirtualClass && isPracticeClassSession;
  const borderColorClass = useBorderedPresentationColoring();
  const { captionText, captionBgColor } = usePresentationCaption();

  // TODO: Should we move query-param based tab indexing into the TabBar component
  // as generic feature? It would be more accessible generally. Most uses of TabBar
  // would convert easily, but PageOverview might require some deeper changes.
  // If we do this, we should also render the tab items as anchor tabs to be fully accessible.
  const activeTab =
    (getQueryParam(TAB_QUERY_PARAM) as (typeof CourseContentTabKeys)[number] | null) ??
    CourseContentTabKeys[0];

  const overlay = getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM);
  const urlForTab = useCallback(
    (tab: (typeof CourseContentTabKeys)[number]) => {
      // This is a fix for switching tabs accidentally closing overlays
      if (overlay) {
        return withQueryParams(sectionHomeUrl(sectionId), { tab, overlay });
      }
      return withQueryParams(sectionHomeUrl(sectionId), { tab });
    },
    [overlay, sectionId],
  );

  const tabs = useMemo(
    () =>
      [
        {
          key: CourseContentTabKeys[0],
          to: urlForTab(CourseContentTabKeys[0]),
          label: (
            <IconText
              iconStart={<BookSectionIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("glossary.content")}
              hideIconOnMd
            />
          ),
          title: t("glossary.content"),
          eventName: "Course Overview - View content",
          panel: !sectionId ? (
            <LoadingScreen />
          ) : (
            <ErrorBoundary>
              <SectionContentView />
            </ErrorBoundary>
          ),
        },
        {
          key: CourseContentTabKeys[1],
          to: urlForTab(CourseContentTabKeys[1]),
          label: (
            <IconText
              iconStart={<OutcomeHashtagIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("glossary.outcomes")}
              hideIconOnMd
            />
          ),
          eventName: "Course Overview - View outcomes",
          title: t("glossary.outcomes"),
          panel: !sectionId ? (
            <LoadingScreen />
          ) : (
            <ErrorBoundary>
              <OutcomesView />
            </ErrorBoundary>
          ),
        },
        {
          key: CourseContentTabKeys[2],
          to: urlForTab(CourseContentTabKeys[2]),
          label: (
            <IconText
              iconStart={<AssessPointsMarkerIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("glossary.assessments")}
              hideIconOnMd
            />
          ),
          title: t("glossary.assessments"),
          eventName: "Course Overview - View assessments",
          panel: !isTemplate ? (
            <ErrorBoundary>
              {sectionId ? <SectionAssessmentMainView /> : <LoadingScreen />}
            </ErrorBoundary>
          ) : null,
        },
        {
          key: CourseContentTabKeys[3],
          to: urlForTab(CourseContentTabKeys[3]),
          label: (
            <IconText
              iconStart={<ReportsIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("session_log_view.title")}
              hideIconOnMd
            />
          ),
          title: t("session_log_view.title"),
          eventName: "Course Overview - View log",
          panel: !isTemplate ? (
            <ErrorBoundary>{sectionId ? <SessionLogView /> : <LoadingScreen />}</ErrorBoundary>
          ) : null,
        },
        {
          key: CourseContentTabKeys[4],
          to: urlForTab(CourseContentTabKeys[4]),
          label: (
            <IconText
              iconStart={<RobotAIIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("glossary.ai_dashboard")}
              hideIconOnMd
            />
          ),
          title: t("glossary.ai_dashboard"),
          eventName: "Course Overview - View AI dashboard",
          panel:
            isMpAdmin && !isTemplate ? (
              <ErrorBoundary>{sectionId ? <AIDashboardView /> : <LoadingScreen />}</ErrorBoundary>
            ) : null,
        },
        {
          key: CourseContentTabKeys[5],
          to: urlForTab(CourseContentTabKeys[5]),
          label: (
            <IconText
              iconStart={<ClockIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("glossary.publication_history")}
              hideIconOnMd
            />
          ),
          title: t("glossary.publication_history"),
          eventName: "Course Overview - View publication history",
          panel:
            !isUnlinked && canAuthorSection ? (
              <ErrorBoundary>
                {sectionId ? <SectionPublicationHistory /> : <LoadingScreen />}
              </ErrorBoundary>
            ) : null,
        },
      ].filter((tab) => tab.panel),
    [urlForTab, sectionId, isTemplate, isUnlinked, canAuthorSection, isMpAdmin],
  );

  const tabIndex = useMemo(() => tabs.map((tab) => tab.key).indexOf(activeTab), [activeTab, tabs]);

  const content =
    // The recording agent automatically follows the presentation or screensharing.
    // When isFollowing is false, it means that there is no presentation or screensharing going on.
    // In this case and in case of a breakout, we just show a simple message.
    isRecordingAgent && (!isFollowing || isInBreakout) ? (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <div className="text-center text-lg font-medium">
          {isInBreakout ? t("recording_agent.in_breakout") : t("recording_agent.no_content")}
        </div>
      </div>
    ) : (
      <>
        {(canShowBothSidebars || !isRightSidePanelOpen) && <SectionTitleBar className="pt-4" />}
        <div className="flex h-full w-full flex-col items-center justify-between md:flex-1 lg:min-w-[470px]">
          <div className="w-full">
            <ErrorBoundary>
              <TabBar className="w-full" selectedIndex={tabIndex} tabs={tabs} />
            </ErrorBoundary>
          </div>
        </div>
      </>
    );

  return (
    <div
      className="group/themable flex h-full w-full flex-row items-stretch justify-center overflow-hidden data-[virtual-class]:rounded-lg"
      data-virtual-class={isInVirtualClass || undefined}
    >
      <div
        className={clsx(
          "relative flex max-h-full w-full justify-center overflow-hidden rounded-[inherit] bg-white",
          shouldShowPresentationCaption && `border-2 ${borderColorClass}`,
        )}
      >
        {shouldShowPresentationCaption && (
          <div className="absolute inset-0">
            <DisplayCaption bgColorClass={captionBgColor} caption={captionText} />
          </div>
        )}

        <div className="flex max-h-full w-full justify-center overflow-y-auto overflow-x-hidden">
          <div className="flex max-h-full w-full max-w-[1200px] flex-col items-center gap-6 p-6 pb-4 md:items-start">
            {content}
          </div>
        </div>

        <LazyScreenshareDisplay />
      </div>

      {!isSmallScreen && (
        <PageSecondarySidebar className="h-full min-w-[392px] max-w-[520px] flex-1 basis-1/3 overflow-y-auto bg-white px-3 py-4 group-data-[virtual-class]/themable:bg-black-tint-15 group-data-[virtual-class]/themable:py-0 group-data-[virtual-class]/themable:pr-0" />
      )}
    </div>
  );
};
